import { forwardRef } from 'react';
import { twMerge } from '@/lib/twMerge';
import PremiumStar from '@/components/Icons/PremiumStar';
import { Button } from '@music-vine/cadence/ui';
import { useHasPermission, useHasRole } from '@/hooks/auth';
import { log } from '@/lib/utils';
import { useWhitelistedChannels } from '@/components/UBSafelist/hooks/useWhitelistedChannels';
import { useOverduePayment } from '@/hooks/billing/useOverduePayment';
import { ShieldAlert, ShieldCheck } from '@music-vine/cadence/icons';
import SafelistedChannels from './SafelistedChannels';
import { ButtonType } from './types';
import { UnprotectedChannelsNotice } from './UnprotectedChannelsNotice';
interface SafelistIconProps {
  className?: string;
}
const SafelistIcon = ({
  className
}: SafelistIconProps) => {
  const isFree = useHasRole('Free');
  const hasSafelistPermission = useHasPermission('safelist_yt');
  const {
    data: whitelistedChannels
  } = useWhitelistedChannels();
  const {
    overduePayment
  } = useOverduePayment();
  const {
    id: overduePaymentId
  } = overduePayment ?? {};
  const hasWhitelistedChannels = whitelistedChannels !== undefined && whitelistedChannels.length > 0;
  const hasUnprotectedChannels = hasWhitelistedChannels && isFree;
  log('hasSafelistPermission', hasSafelistPermission);
  if (isFree) {
    return <PremiumStar className={twMerge('aspect-square size-4 text-white', className)} hideBorder />;
  }
  if (hasSafelistPermission) {
    if (overduePaymentId) {
      return <ShieldAlert className={twMerge('text-red-600 dark:text-red-400', className)} />;
    }
    if (!hasUnprotectedChannels) {
      return <ShieldCheck className={twMerge('text-green-600 dark:text-green-400', className)} />;
    }
    return null;
  }
};
interface SafelistButtonProps {
  className?: string;
  safelistButtonType: ButtonType;
}
const SafelistButton = forwardRef<HTMLButtonElement, SafelistButtonProps>(({
  safelistButtonType = ButtonType.Button,
  ...props
}, ref) => {
  if (safelistButtonType === ButtonType.Skeleton) {
    return <Button className={twMerge('w-[155px] animate-pulse bg-gray-50 dark:bg-gray-900', props.className)} disabled size="sm" variant="light" />;
  }
  if (safelistButtonType === ButtonType.Safelist) {
    return <UnprotectedChannelsNotice>
          <SafelistedChannels ref={ref} {...props} />
        </UnprotectedChannelsNotice>;
  }
  if (safelistButtonType === ButtonType.Button) {
    return <Button className={props.className} fontSize={{
      lg: 'sm'
    }} ref={ref} size={{
      default: 'sm',
      lg: 'default',
      xl: 'lg'
    }} variant="light" {...props}>
          <SafelistIcon className="size-3 md:size-4" />
          {'Safelist your channel'}
        </Button>;
  }
  return null;
});
SafelistButton.displayName = 'SafelistButton';
export default SafelistButton;